/* Copyright 2011-2020 The MathWorks, Inc. */

define([
    "dojo/aspect",
    "dojo/debounce",
    "dojo/_base/lang",
    "dojo/_base/declare",
    "dijit/registry",
    "dijit/popup",
    "../constants/TypeConstants",
    "../WidgetFactory",
    "../hashes/ControlEventsHash",
    "../hashes/ComponentPropertiesHash",
    "../hashes/PeerNodeWidgetEventsHash",
    "../hashes/PeerNodeWidgetPropertiesHash",
    "../hashes/listOfNewWidgets",
    "MW/toolstrip/hashes/ToolstripCollapseTriggers",
    "MW/toolstrip/constants/DisplayStateConstants",
    "MW/toolstrip/ToolstripUtils",
    "mw-log/Log",
    "mw-utils/Utils",
    "./LazyLoadingWhitelist"
], function (aspect, debounce, lang, declare, registry, popup, TypeConstants, WidgetFactory, ControlEventsHash,
             ComponentPropertiesHash, PeerNodeWidgetEventsHash, PeerNodeWidgetPropertiesHash, listOfNewWidgets,
             ToolstripCollapseTriggers, DisplayStateConstants, ToolstripUtils, Log, Utils, LazyLoadingWhitelist) {

    return declare(null, {

        /**
         * The Widget Manager listens for updates happening in the peer nodes and reacts to
         * them by creating, destroying, hiding, showing and updating the corresponding widgets.
         * @constructs
         * @param {object} args 'modelManager' is a required property.
         * <li> modelManager: The model manager which deals with the CRUD of peer nodes.</li>
         */
        constructor: function (args) {
            var that = this;

            this._aspectMap = {/* aspectId: aspectInstance */};
            this._widgetsWithActionIdMap = {/* actionId: [widgetInstance] */};

            args = args || {};

            Log.assert(args.modelManager, "Model Manager needs to be passed in!");

            this._modelManager = args.modelManager;
            this._openPopups = [];

            // This is used when in MATLABDriven mode to keep track of refreshGallery events coming from MATLAB
            this._galleryRefreshMap = {};

            this._addAspect(registry, "add", lang.hitch(this, function (widget) {
                if (widget.actionId && ComponentPropertiesHash[widget.type]) {
                    if (this._widgetsWithActionIdMap[widget.actionId]) {
                        this._widgetsWithActionIdMap[widget.actionId].push(widget);
                    } else {
                        this._widgetsWithActionIdMap[widget.actionId] = [widget];
                    }
                }
            }), "after");
            this._addAspect(registry, "remove", lang.hitch(this, function (widgetId) {
                var actionId,
                    widget = registry.byId(widgetId);

                if (widget) {
                    actionId = widget.actionId;
                }

                if (this._widgetsWithActionIdMap[actionId]) {
                    if (this._widgetsWithActionIdMap[actionId].length === 1) {
                        delete this._widgetsWithActionIdMap[actionId];
                    } else {
                        this._widgetsWithActionIdMap[actionId].splice(this._widgetsWithActionIdMap[actionId].indexOf(widget), 1);
                    }
                }
            }), "before");
            this._addAspect(this._modelManager, "onChildAdd", function (event) {
                that.childAddedObserver(event);
            }, "after");
            this._addAspect(this._modelManager, "onChildDestroy", function (event) {
                that.childDestroyedObserver(event);
            }, "after");
            this._addAspect(this._modelManager, "onPropertySet", function (event) {
                that.propertySetObserver(event);
            }, "after");
            this._addAspect(this._modelManager, "onChildMove", function (event) {
                that.childMovedObserver(event);
            }, "after");
            this._addAspect(this._modelManager, "onPeerEvent", function (event) {
                that.peerEventObserver(event);
            }, "after");
            this._addAspect(this._modelManager.actionService, "onActionChange",
                lang.hitch(this, this._onActionChange), "after");
            this._addAspect(this._modelManager.actionService, "onActionAdd",
                lang.hitch(this, this._onActionAdd), "after");
            this._addAspect(this._modelManager.actionService, "onPeerEvent",
                lang.hitch(this, this._onActionPeerEvent), "after");
        },

        _getAspectId: function (target) {
            if (target instanceof Object) {
                if (target.aspectId) {
                    // No-op
                } else if (target.id) {
                    target.aspectId = target.id;
                } else {
                    target.aspectId = Utils.generateUuid();
                }

                return target.aspectId;
            } else {
                throw new Error('WidgetManager only supports widgets.');
            }
        },
        _getAspectKey: function (target, methodName, advisingFunction) {
            var aspectId = this._getAspectId(target);

            if (aspectId && methodName && (typeof advisingFunction === "function")) {
                return aspectId + "_" + methodName + "_" + advisingFunction.toString();
            } else {
                throw new Error('Invalid input.');
            }
        },
        _getAssociatedAspects: function (target) {
            var aspectId = this._getAspectId(target),
                associatedAspects = [];

            Object.keys(this._aspectMap).forEach(function (key) {
                if (key.indexOf(aspectId + "_") === 0) {
                    associatedAspects.push(key);
                }
            }.bind(this));

            return associatedAspects;
        },
        _addAspect: function (target, methodName, advisingFunction, invocation) {
            var aspectKey = this._getAspectKey(target, methodName, advisingFunction);
            if (aspectKey && !this._aspectMap.hasOwnProperty(aspectKey)) {
                if (invocation === "after") {
                    this._aspectMap[aspectKey] = aspect.after(target, methodName, advisingFunction, true);
                } else if (invocation === "before") {
                    this._aspectMap[aspectKey] = aspect.before(target, methodName, advisingFunction);
                }
            }

            return aspectKey;
        },
        _removeAspect: function (aspectKey) {
            if (this._aspectMap.hasOwnProperty(aspectKey)) {
                this._aspectMap[aspectKey].remove();
                delete this._aspectMap[aspectKey];
            }
        },

        /**
         * Listener attached to the 'childAdded' event in the Peer Node.
         * @param event {object} The event data which is received from the 'childAdded' event.
         */
        childAddedObserver: function (event) {
            var peerNode, widget, widgetProps, peerNodeId, toolstripId, parentId, selectedTab, widgetTab, isGalleryFavoriteCategory;

            peerNode = event.data.child;

            if (ToolstripUtils.hasNoCorrespondingWidget(peerNode.getType()) || peerNode.getType() === TypeConstants.STATUS_BAR_ROOT) {
                return;
            }

            widgetProps = this._getWidgetProperties(peerNode);
            peerNodeId = peerNode.getId();
            // Add on 'id' so that the id of the widget is the same as the corresponding peer node.
            widgetProps.id = peerNodeId.toString();

            // Be robust to multiple onChildAdd events firing from the Peer Model.
            if (registry.byId(widgetProps.id)) {
                return;
            }

            //check for empty tag: If there is empty tag then there is no need of creating this map
            // g1698140: If the widget was added using "addGalleryItemToFavorites", 
			// we don't want to overwrite the original widget.
            if (widgetProps.tag !== '' && event.originator ? (event.originator.source ? (event.originator.source.indexOf("addGalleryItemToFavorites") < 0) : true) : true) {
                this._modelManager.tagToPeerNodeIdMap[widgetProps.tag] = peerNodeId;
            }

            // Defer the building of the widgets if lazyLoading flag is set to true
            if (this._modelManager.lazyLoading) {
                if (LazyLoadingWhitelist.indexOf(widgetProps.type) > -1) {
                    // Build a widget using the details in the peer node properties.
                    widget = this._buildWidget(widgetProps, peerNode);
                    this._addChild(widget, peerNode);
                    // build the qab dropdown's popup list
                    this._buildAndAddDescendants(widget, widgetProps);
                    if (widgetProps.type === TypeConstants.TOOLSTRIP) {
                        widget.tabContainer.watch("selectedChildWidget", lang.hitch(this, this._selectedTabChangedCallback));
                    }
                } else {
                    toolstripId = this._modelManager._toolstrip.id;
                    selectedTab = this._modelManager.get(toolstripId, "selectedTab");

                    if (selectedTab) {
                        widgetTab = this._modelManager._findAncestorId(TypeConstants.TAB, widgetProps.id);
                        parentId = this._modelManager.getParentId(widgetProps.id);
                        isGalleryFavoriteCategory = parentId ? this._modelManager.get(parentId, "type") === TypeConstants.GALLERY_FAVORITE_CATEGORY : false;

                        // adding exception if the widget is part of gallery favorite category as it is tricky to find out it's ancestor
                        if (selectedTab === widgetTab || isGalleryFavoriteCategory) {
                            this._buildWidgetAndAddChildren(peerNode);
                        }
                    }
                }
            } else {
                // Build a widget using the details in the peer node properties.
                widget = this._buildWidget(widgetProps, peerNode);
                this._addChild(widget, peerNode, event.originator);
            }
        },

        _addChild: function (widget, peerNode, originator) {
            var parentNode = peerNode.getParent(),
                widgetIndex = parentNode.getChildIndex(peerNode),
                parentWidget = this._getWidgetForPeerNode(parentNode);

            if (widget.get("type") === TypeConstants.GALLERY) {
                this.refreshGallery(peerNode.getId());
                this._modelManager.addEventCallback(peerNode.getId(), debounce(function (data) {
                    if (data.eventType === "refresh") {
                        this.refreshGallery(peerNode.getId());
                    }
                }.bind(this), 100));
            } else if (widget.get("type") === TypeConstants.GALLERY_POPUP) {
                var galleryNodes = this._modelManager.manager.getByProperty("galleryPopupId", widget.id);
                galleryNodes.forEach(function (galleryNode) {
                    registry.byId(galleryNode.getId()).setupGalleryPopupListener(widget);
                });
            }

            // There will not be a parentWidget for the orphan root peer node, in that case the
            // widget is simply built but not added to a parent.
            if (parentWidget) {
                parentWidget.addChild(widget, widgetIndex, originator);
            }
        },

        /**
         * The following function is called whenever a tab change happens.
         *
         * @param {string} name - name of the event. 'selectedChildWidget' in this case
         * @param {object} oval - the previous selected tab
         * @param {object} nval - the current selected tab
         * @private
         */
        _selectedTabChangedCallback: function(name, oval, nval) {
            var dirtyFlag, tabPeerNode, sectionPeerNodes, childrenNodes;

            tabPeerNode = this._modelManager.manager.getById(nval.id);
            dirtyFlag = this._checkAndUpdateTab(tabPeerNode);

            // resize the toolstrip if the any widget was built
            if (dirtyFlag) {
                registry.byId(this._modelManager._toolstrip.id).resize();
            }
        },

        /**
         * It goes through the contents of the tab and builds any unbuilt widget(s).
         *
         * @param {object} peerNode - the tab's peerNode
         * @returns {boolean} whether the toolstrip tab was dirty or not
         * @private
         */
        _checkAndUpdateTab: function (peerNode) {
            var dirtyFlag = false, children;

            children = peerNode.getChildren();
            if (children && children.length !== 0) {
                // loop through the list of sections. if the section is built then
                // do nothing else build the section and its contents.
                children.forEach(lang.hitch(this, function (child) {
                    if (!registry.byId(child.getId())) {
                        // If widget doesn't exist then recursively build the contents of the tab
                        this._buildWidgetAndAddChildren(child);
                        // Setting the dirtyFlag as a section was built
                        dirtyFlag = true;
                    } else {
                        this._checkAndUpdateTab(child);
                    }
                }));
            }

            return dirtyFlag;
        },

        /**
         * It builds the widget and its children.
         *
         * @param {object} peerNode - any peerNode
         * @private
         */
        _buildWidgetAndAddChildren: function (peerNode) {
            var widgetProps = this._getWidgetProperties(peerNode),
                peerNodeId = peerNode.getId(),
                children = peerNode.getChildren();

            // Add on 'id' so that the id of the widget is the same as the corresponding peer node.
            widgetProps.id = peerNodeId.toString();

            var widget = registry.byId(widgetProps.id) || this._buildWidget(widgetProps, peerNode);
            this._addChild(widget, peerNode);

            if (children) {
                children.forEach(lang.hitch(this, function (child) {
                    this._buildWidgetAndAddChildren(child);
                }));
            }

            // build descendants e.g popups, footers etc
            this._buildAndAddDescendants(widget, widgetProps);
        },

        /**
         * It builds the widget's descendants. footer and favorite category are not direct children of the widget,
         * but rather descendants.
         *
         * @param {object} widget - widget for which to build the descendants
         * @param {object} widgetProps - widget's properties
         * @private
         */
        _buildAndAddDescendants: function (widget, widgetProps) {
            var footerId, footerNode, favCategoryId, favCategoryPeerNode;

            if (widgetProps.footerId) {
                footerId = widgetProps.footerId;

                // If widget already exists then don't build it again.
                if (registry.byId(footerId)) {
                    return;
                }

                footerNode = this._modelManager.manager.getById(footerId);
                this._buildWidgetAndAddChildren(footerNode);

                // footer is not parented to the gallery popup.
                // it needs to be attached by calling it setter
                registry.byId(widgetProps.id).set("footerId", widgetProps.footerId);
            }

            if (widgetProps.favCategoryId) {
                favCategoryId = widgetProps.favCategoryId;

                // If widget already exists then don't build it again.
                if (registry.byId(favCategoryId)) {
                    return;
                }

                favCategoryPeerNode = this._modelManager.manager.getById(favCategoryId);
                this._buildWidgetAndAddChildren(favCategoryPeerNode);

                // favorite category is not parented to the gallery popup.
                // it needs to be attached by calling it setter
                registry.byId(widgetProps.id).set("favCategoryId", widgetProps.favCategoryId);
            }

            // Lazy load the popups
            this._buildAndAddPopups(widget, widgetProps);
        },

        /**
         * It builds the widget's popups.
         *
         * @param {object} widget - widget for which to build the descendants
         * @param {object} widgetProps - widget's properties
         * @private
         */
        _buildAndAddPopups: function (widget, widgetProps) {
            var listOfWidgetsHavingPopup = [TypeConstants.QAB_TOGGLE_SPLIT_BUTTON,
                                            TypeConstants.QAB_DROP_DOWN_BUTTON,
                                            TypeConstants.QAB_SPLIT_BUTTON,
                                            TypeConstants.LIST_ITEM_WITH_POPUP,
                                            TypeConstants.TOGGLE_SPLIT_BUTTON,
                                            TypeConstants.DROP_DOWN_BUTTON,
                                            TypeConstants.SPLIT_BUTTON,
                                            TypeConstants.GALLERY];

            var that = this, popupListId, popupListNode, popupChildren;

            if (listOfWidgetsHavingPopup.indexOf(widgetProps.type) > -1) {
                // The first time the widget is opened either by clicking the menuNode
                // or by programatically calling uiBuilder.showPopup, build it's corresponding popup
                this._addAspect(widget,"_showMenu", function () {
                    if (this.menuId) {
                        popupListId = this.menuId;
                        
                        // If widget already exists and if it's not a dynamic menu, then don't build it again.
                        if (registry.byId(popupListId) && !widget.get("hasDynamicMenu")) {
                            return;
                        }

                        popupListNode = that._modelManager.manager.getById(popupListId);
                        that._buildWidgetAndAddChildren(popupListNode);
                    }
                }, "before");
                // Build gallery popup, before the first time it is opened
                this._addAspect(widget, "openDropDown", function () {
                    if (this.galleryPopupId) {
                        popupListId = this.galleryPopupId;

                        // If widget already exists then don't build it again.
                        if (registry.byId(popupListId)) {
                            return;
                        }

                        popupListNode = that._modelManager.manager.getById(popupListId);
                        that._buildWidgetAndAddChildren(popupListNode);
                    }
                }, "before");

                // if the dropdown widget has dynamic menu
                widget.on("dropdownclick", function () {
                    var widget;

                    widget = registry.byNode(this);
                    popupListId = widget.menuId;
                    popupListNode = that._modelManager.manager.getById(popupListId);
                    popupChildren = popupListNode.getChildren();

                    popupChildren.forEach(lang.hitch(that, function (childNode) {
                        // If widget already exists then don't build it again.
                        // TODO: remove the expection once we come up with a new design for GoTo dropdown (see g1938977)
                        if (!registry.byId(childNode.getId()) || childNode.getType() === TypeConstants.POPUP_LIST_PANEL) {
                            this._buildWidgetAndAddChildren(childNode);
                        }
                    }));
                })
            }
        },

        addOutsidePeerModel: function (childId, parentId) {
            var childNode  = this._modelManager.manager.getById(childId);
            var parentNode = this._modelManager.manager.getById(parentId);
            var widgetProps = this._getWidgetProperties(childNode);
            widgetProps.id = childNode.getId().toString();
            var widget =  registry.byId(widgetProps.id) || this._buildWidget(widgetProps, childNode);
            var parentWidget = this._getWidgetForPeerNode(parentNode);
            if (parentWidget) {
                parentWidget.addChild(widget);
            }
        },

        removeOutsidePeerModel: function (childId) {
            var childNode  = this._modelManager.manager.getById(childId);
            var childWidget = this._getWidgetForPeerNode(childNode);
            this._getAssociatedAspects(childWidget).forEach(function (aspectKey) {
                this._removeAspect(aspectKey);
            }.bind(this));
            var parentWidget = childWidget.getParent();
            if (parentWidget) {
                parentWidget.removeChild(childWidget);
            }
            //childWidget.destroyRecursive();
        },

        _buildWidget: function (widgetProps, peerNode) {
            if (widgetProps.type === TypeConstants.QA_GROUP || widgetProps.type === TypeConstants.QAB) {
                widgetProps.enableQABCustomization = this._modelManager.enableQABCustomization;
            } else if (widgetProps.type === TypeConstants.TOOLSTRIP) {
                widgetProps.enableQABCustomization = this._modelManager.enableQABCustomization;
                widgetProps.qabAuthoringMode = this._modelManager._qabAuthoringMode;
            }

            var widget = WidgetFactory.create(widgetProps),
                that = this;

            this._addWatchToWidgetProperties(widget, peerNode);
            this._attachPopupMonitor(widget);

            this._hookupEvents(widget, peerNode);

            this._listenForToolstripCollapseTrigger(widget);

            // Every time a widget is removed, its state (hovering and active) and the state of all it's descendants
            // should be reset to false.
            this._addAspect(widget, "removeChild", function (child) {
                that._resetStateRecursive(child);
            });

            return widget;
        },

        // Different components in the toolstrip can trigger the collapsing of their parent Toolstrip based on either an
        // event or a property change. This method attaches listeners to those triggers.
        _listenForToolstripCollapseTrigger: function (widget) {
            var that = this, toolstripCollapseData = ToolstripCollapseTriggers[widget.get("type")];

            if (!toolstripCollapseData) {
                return;
            }

            // When one of the collapse triggering events is fired, collapse the parent toolstrip.
            toolstripCollapseData.events.forEach(function (event) {
                if (listOfNewWidgets.indexOf(widget.type) > -1) {
                    widget.on(PeerNodeWidgetEventsHash[widget.type][event], function () {
                        that._collapseParents(widget);
                    });
                } else {
                    widget.own(aspect.after(widget, event, function () {
                        that._collapseParents(widget);
                    }));
                }
            });

            // When one of the collapse triggering property changes, collapse the parent toolstrip.
            widget.watch(function (attr) {
                if (toolstripCollapseData.properties.indexOf(attr) > -1) {
                    // g1986757: Only collapse parents when the property is changed due to a user interaction.
                    var originator = widget._treatAsUserEvent;  // TODO: Either have a public API on the widget to get
                                                                // this property or make this property public
                    if (originator && originator.source === "UI") {
                        that._collapseParents(widget);
                    }
                }
            });
        },

        _collapseParents: function (widget) {
            this._collapseParentSection(widget);
            this._collapseParentToolstrip(widget);
            this._collapseParentQABOverflowPopup(widget);
        },

        _collapseParentSection: function (widget) {
            var section = ToolstripUtils.findParentSection(widget);
            if (section) {
                section.closePopup();
            }
        },

        // Collapses the parent Toolstrip if it is in an expanded on top state
        _collapseParentToolstrip: function (widget) {
            var toolstrip = ToolstripUtils.findParentToolstrip(widget);
            // widgets inside footer cell  and collapse toolstrip
            if (toolstrip && toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP && widget.getParent().type !== "FooterCell") {
                toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
            }
        },

        _collapseParentQABOverflowPopup: function (widget) {
            var qabOverflowPopup = document.querySelector('[data-tag="qabOverflowPopup"]');
            if (qabOverflowPopup && qabOverflowPopup.contains(widget.domNode)) {
                var qabOverflowDropDownNode = document.querySelector('[data-tag="qabOverflowButton"]');
                if (qabOverflowDropDownNode) {
                    var qabOverflowDropDown = registry.getEnclosingWidget(qabOverflowDropDownNode);
                    if (qabOverflowDropDown && qabOverflowDropDown.closeMenu) {
                        qabOverflowDropDown.closeMenu();
                    }
                }
            };
        },

        // Keep track of the currently open popups.
        _attachPopupMonitor: function (widget) {
            var that = this, popup;

            switch (widget.get("type")) {
            case TypeConstants.POPUP_LIST:
                popup = widget;
                break;
            case TypeConstants.COMBO_BOX:
                popup = widget.dropDown;
                break;
            case TypeConstants.GALLERY_POPUP:
                popup = widget;
                break;
            case TypeConstants.SECTION:
                popup = widget.getPopup();
                break;
            }

            if (popup) {
                this._addAspect(popup, "onOpen", function () {
                    that._onPopupOpen(popup);
                });

                this._addAspect(popup, "onClose", function () {
                    that._onPopupClose();
                });
            }
        },

        _getWidgetProperties: function (peerNode) {
            var allProps = this._modelManager.getAllProperties(peerNode.getId()), propsHash, components;

            Object.keys(allProps).forEach(function (prop) {
                propsHash = PeerNodeWidgetPropertiesHash[peerNode.getType()];
                if (propsHash && propsHash[prop]) {
                    components = ComponentPropertiesHash[peerNode.getType()][prop];
                    if (components.shouldBeFlipped) {
                        //Flip the boolean value only. So no need of clone
                        allProps[propsHash[prop]] = !allProps[prop];
                    } else {
                        allProps[propsHash[prop]] = lang.clone(allProps[prop]);
                    }
                    delete allProps[prop];
                }
            });
            allProps.type = peerNode.getType();
            return allProps;
        },

        /**
         * Listener attached to the 'childRemoved' event in the Peer Node.
         * @param event {object} The event data which is received from the 'childRemoved' event.
         */
        childDestroyedObserver: function (event) {
            var widget, parentWidget, peerNode, parentNode, peerNodeId;

            peerNode = event.data.child;
            parentNode = event.target;
            peerNodeId = peerNode.getId();

            widget = this._getWidgetForPeerNode(peerNode);

            // If the node being destroyed is one of the root nodes then it wont have a
            // corresponding widget. So ignore it and return.
            if (!widget) {
                return;
            }

            this._getAssociatedAspects(widget).forEach(function (aspectKey) {
                this._removeAspect(aspectKey);
            }.bind(this));

            // Remove the id from map by looping through all tags
            Object.keys(this._modelManager.tagToPeerNodeIdMap).forEach(lang.hitch(this, function(key) {
                if(this._modelManager.tagToPeerNodeIdMap[key] === peerNodeId) {
                    delete this._modelManager.tagToPeerNodeIdMap[key];
                }
            }));

            parentWidget = this._getWidgetForPeerNode(parentNode);

            // We do not want to destroy the figure toolbars and menu bars since their life cycle is
            // controlled by MGG. So we remove them from the wrappers before doing a recursive
            // destruction.
            if (ToolstripUtils.isAMenubarWrapper(widget.get("type")) ||
                ToolstripUtils.isAToolbarWrapper(widget.get("type"))) {
                widget.getChildren().forEach(function (child) {
                    widget.removeChild(child);
                });
            }

            // g1931963: For a favorited GalleryItem and ToggleGalleryItem getting destroyed, the corresponding
            // widget in the favorites category should also be destroyed.
            if ((widget.get("type") === TypeConstants.GALLERY_ITEM || widget.get("type") === TypeConstants.TOGGLE_GALLERY_ITEM)
                && widget.get("isFavorite") === true && parentWidget && parentWidget.get("type") !== "GalleryFavoriteCategory") {

                var actionId = peerNode.getProperty("actionId");
                var favCategoryId;

                if (parentNode.getParent && parentNode.getParent()) {
                    favCategoryId = parentNode.getParent().getProperty("favCategoryId");

                    if (favCategoryId) {
                        var favCategoryItem = this._modelManager.manager.getByProperty("actionId", actionId,
                            this._modelManager.manager.getById(favCategoryId))[0];

                        if (favCategoryItem) {
                            var favCategoryItemId = favCategoryItem.getId();
                            if (favCategoryItemId) {
                                this._modelManager.destroy(favCategoryItemId, false);
                            }
                        }
                    }
                }
            }

            if (parentWidget) {
                parentWidget.removeChild(widget, event.originator);
            }
            widget.destroyRecursive();
        },

        /**
         * Listener attached to the 'childMoved' event in the Peer model manager.
         * @param event {object} The event data which is received from the 'childMoved' event.
         */
        childMovedObserver: function (event) {
            var peerNode, childWidget, toolstripId, widgetTabId, widgetTabGroupNode, widgetTabNodes, selectedTabId;

            peerNode = event.target;
            childWidget = this._getWidgetForPeerNode(peerNode);

            if (childWidget) {
                this._moveChild(event);
            } else {
                toolstripId = this._modelManager._toolstrip.id;
                selectedTabId = this._modelManager.get(toolstripId, "selectedTab");
                widgetTabId   = this._modelManager._findAncestorId(TypeConstants.TAB, peerNode.getId());

                if (selectedTabId === widgetTabId) {
                    this._buildWidgetAndAddChildren(peerNode);
                }
            }
        },

        _moveChild: function (event) {
            var oldParentWidget, newParentWidget, childWidget, originator;

            childWidget     = this._getWidgetForPeerNode(event.target);
            oldParentWidget = this._getWidgetForPeerNode(event.data.oldParent);
            newParentWidget = this._getWidgetForPeerNode(event.data.newParent);
            originator      = event.originator;

            // TODO: Figure out a better way to be able to pass originator value from the MATLAB side when MCOS API is
            // used. Currently, there does not seem to be a way to pass originator value when MCOS API is used. The
            // originator value comes from the peer model code and is always an empty object.
            if (this._modelManager.isMATLABDriven) {
                originator = {source: "UI"};
            }

            if (oldParentWidget) {
                oldParentWidget.removeChild(childWidget, originator);
            }

            if (newParentWidget) {
                newParentWidget.addChild(childWidget, event.data.newIndex, originator);
            } else {
                // g1913457: We are orphaning the widget, so we should clean up any CSS states
                // possibly left behind by _CSSStateMixin.js
                if (childWidget.hasOwnProperty("hovering")) {
                    childWidget.set("hovering", false);
                }
                if (childWidget.hasOwnProperty("active")) {
                    childWidget.set("active", false);
                }
            }
        },

        /**
         * Listener attached to the 'propertySet' event in the Peer Node.
         * @param event {object} The event data which is received from the 'propertySet' event.
         */
        propertySetObserver: function (event) {
            var widget = this._getWidgetForPeerNode(event.target), widgetProps, oldTag, oldTagId;
            if (widget) {

                //Update Peer Node Id map when tag is set
                if(event.data.key === "tag"){
                    oldTag = widget.get("tag");
                    oldTagId = this._modelManager.tagToPeerNodeIdMap[oldTag];
                    // Delete old tag
                    delete this._modelManager.tagToPeerNodeIdMap[oldTag];
                    // add new Tag
                    // TODO: explore getId of event/widget
                    this._modelManager.tagToPeerNodeIdMap[event.data.newValue] = oldTagId;
                }

                this._setWidgetProperty(widget, event.data.key, event.data.newValue, event.originator);
                // If the action associated with the component node is changed, then the widget should
                // update itself with the new properties from the new Action.
                if (event.data.key === "actionId") {
                    widgetProps = this._getWidgetProperties(event.target);
                    Object.keys(widgetProps).forEach(function (prop) {
                        if (!widget._restrictModelUpdate) {
                            widgetProps = this._getWidgetProperties(event.target);
                            widget.set(prop, widgetProps[prop]);
                        }
                    }, this);

                    // update the map according to the new actionId
                    if (this._widgetsWithActionIdMap[event.data.newValue]) {
                        this._widgetsWithActionIdMap[event.data.newValue].push(widget);
                    } else {
                        this._widgetsWithActionIdMap[event.data.newValue] = [widget];
                    }

                    // update the map according to the old actionId
                    if (this._widgetsWithActionIdMap[event.data.oldValue]) {
                        if (this._widgetsWithActionIdMap[event.data.oldValue].length === 1) {
                            delete this._widgetsWithActionIdMap[event.data.oldValue];
                        } else {
                            this._widgetsWithActionIdMap[event.data.oldValue].splice(this._widgetsWithActionIdMap[event.data.oldValue].indexOf(widget), 1);
                        }
                    }
                }
            }
        },

        _getWidgetsWithActionId: function (actionId) {
            if (this._widgetsWithActionIdMap[actionId]) {
                return this._widgetsWithActionIdMap[actionId];
            } else {
                return [];
            }
        },

        _onActionChange: function (eventData) {
            // Avoid expensive registry.toArray call for when the initial id of the action is
            // being set in ActionDataService
            if (eventData.key === "id") {
                return;
            }
            this._getWidgetsWithActionId(eventData.id).forEach(function (widget) {
                if (!widget._destroyed) {
                    this._setWidgetProperty(widget, eventData.key, eventData.newValue, eventData.originator);
                }
            }, this);
        },

        // If action is added after the widget was already created, fill in the missing widget
        // properties from the action.
        _onActionAdd: function (eventData) {
            // This scenario would happen only for MATLAB driven use case since, the action and
            // components are on different Peer Model Channels, so avoid expensive
            // registry.toArray call
            if (!this._modelManager.isMATLABDriven) {
                return;
            }
            var actionObj = this._modelManager.actionService.getAction(eventData.id);
            var actionKeys = Object.keys(actionObj);

            this._getWidgetsWithActionId(eventData.id).forEach(function (widget) {
                actionKeys.forEach(function (key) {
                    if (!widget._destroyed) {
                        this._setWidgetProperty(widget, key, actionObj[key], eventData.originator);
                    }
                }, this);
            }, this);
        },

        _setWidgetProperty: function (widget, property, value, originator) {
            var widgetProperty = property, propsHash = PeerNodeWidgetPropertiesHash[widget.get("type")], propMetaData;

            // If it is not a valid property then do nothing.
            if (Object.keys(ComponentPropertiesHash[widget.type]).indexOf(property) === -1) {
                return;
            }

            if (propsHash && propsHash[property]) {
                widgetProperty = propsHash[property];
            }
            propMetaData = ComponentPropertiesHash[widget.type][property];
            if (propMetaData.shouldBeFlipped) {
                widget.set(widgetProperty, !value, originator);
            } else {
                widget.set(widgetProperty, value, originator);
            }
        },

        /**
         * Listener attached to the 'peerEvent' event in the Peer Node.
         * @param event {object} The event data which is received from the 'peerEvent' event.
         */
        peerEventObserver: function (event) {
            switch (event.data.eventType) {
            case "placeToolStrip":
                this._modelManager.placeToolstripById(event.target.getId(), event.data.hostId);
                break;
            case "showPopup":
                this._modelManager.showPopup(event.target.getId(), event.data.popupId);
                break;
            case "refreshGallery":
                this._refreshGalleryFromMATLAB(event.data.galleryId);
                break;
            }
        },

        _refreshGalleryFromMATLAB: function(galleryId) {
            if (this._galleryRefreshMap[galleryId]) {
                this._galleryRefreshMap[galleryId] += 1;
            } else {
                this._galleryRefreshMap[galleryId] = 1;
            }

            // If both the action channel and the UIBuilder channel have received the galleryRefresh event, then refresh
            // the gallery
            if (this._galleryRefreshMap[galleryId] === 2) {
                this.refreshGallery(galleryId);
                delete this._galleryRefreshMap[galleryId];
            }
        },

        _onActionPeerEvent: function (event) {
            switch (event.data.eventType) {
            case "refreshGallery":
                this._refreshGalleryFromMATLAB(event.data.galleryId);
                break;
            }
        },

        /**
         * Helper method used to watch the properties in the widget and update the corresponding
         * peer node when they change.
         * @param widget
         * @param peerNode
         */
        _addWatchToWidgetProperties: function (widget, peerNode) {
            var peerNodeProperty, that = this, propMetaData;
            var propsHash = PeerNodeWidgetPropertiesHash[peerNode.getType()];
            var peerNodeProperties = ComponentPropertiesHash[peerNode.getType()];
            widget.watch(function (attr, oldVal, newVal) {
                if (!that._modelManager.exists(peerNode.getId())) {
                    return;
                }
                peerNodeProperty = attr;
                if (propsHash && propsHash[attr]) {
                    peerNodeProperty = propsHash[attr];
                }

                if (Object.keys(peerNodeProperties).indexOf(peerNodeProperty) === -1) {
                    return;
                }
                propMetaData = ComponentPropertiesHash[peerNode.getType()][peerNodeProperty];
                if (propMetaData.shouldBeFlipped) {
                    newVal = !newVal;
                }

                var isAttrAndWidgetEligibleForProgEventDistinction = function (widget, attr) {
                    // TODO: The below conditions might keep growing as new gecks arise for not suppressing the events
                    // on programmatic API calls. Ideally we should ensure that events are not fired for any
                    // programmatic calls for all the widgets.
                    return attr === "checked" || attr === "isInQAB" || (attr === "value" && widget.get("type") === TypeConstants.COMBO_BOX);
                };

                if (isAttrAndWidgetEligibleForProgEventDistinction(this, attr) && !this._treatAsUserEvent) {
                    that._modelManager.set(peerNode.getId(), peerNodeProperty, newVal, {source: "ProgrammaticEvent"});
                } else {
                    that._modelManager.set(peerNode.getId(), peerNodeProperty, newVal, {source: "UI"});
                }
            });
        },

        /**
         * Helper method to hook up event handlers to widget.
         * @param widget
         * @param peerNode
         */
        _hookupEvents: function (widget, peerNode) {
            var that = this, type = peerNode.getType(), eventsMap = ControlEventsHash[type];

            if (!eventsMap) {
                return;
            }

            function attachListener(events, target) {
                var method = "aspect";
                if (listOfNewWidgets.indexOf(type) > -1) {
                    method = "on";
                }

                events.forEach(function (event) {

                    function eventCallback(data) {
                        var newData = {};
                        if (method === "aspect") {
                            newData = data || {};
                        } else {
                            if(data && data.mwEventData) {
                                lang.mixin (newData, data.mwEventData);
                            }
                        }
                        newData.eventType = event;
                        if (this instanceof Element && this.hasAttribute("widgetid")) {
                            newData.id = this.getAttribute("widgetid");
                        }

                        if (target === "action") {
                            that._modelManager.actionService.executeAction(peerNode.getProperty("actionId"), newData);
                        } else {
                            peerNode.dispatchEvent("peerEvent", peerNode, newData);
                        }
                    }

                    if (method === "aspect") {
                        widget.own(aspect.after(widget, event, eventCallback, true));
                    } else {
                        var widgetEvent = event;
                        if (PeerNodeWidgetEventsHash[type] && PeerNodeWidgetEventsHash[type][event]) {
                           widgetEvent = PeerNodeWidgetEventsHash[type][event];
                        }
                        widget.on(widgetEvent, eventCallback);
                    }
                });

            }

            attachListener(eventsMap.actionEvents, "action");
            attachListener(eventsMap.componentEvents, "peerNode");
        },

        /**
         * Finds and returns the widget corresponding to the given peer node.
         * @param peerNode
         */
        _getWidgetForPeerNode: function (peerNode) {
            return registry.byId(peerNode.getId().toString());
        },

        /**
         * An event fired when any popup is closed.
         */
        _onPopupClose: function () {
            this._openPopups.pop();
            this.onOpenPopupCountChange(this._openPopups.length, this._openPopups);
        },

        /**
         * An event fired when any popup is opened.
         */
        _onPopupOpen: function (widget) {
            this._openPopups.push(widget);
            this.onOpenPopupCountChange(this._openPopups.length, this._openPopups);
        },

        /**
         * An event fired when any popup is open or closed.
         */
        onOpenPopupCountChange: function (openPopupCount, openPopups) {
        },

        /**
         * Returns true if there is at least one open popup in the stack and it belongs to the toolstrip.
         */
        isAnyPopupOpen: function () {
            return popup._stack.length > 0 && this._modelManager.exists(popup._stack[0].widget.id);
        },

        /**
         * Close all currently open popups that belong to the toolstrip.
         */
        closeAllOpenPopups: function () {
            if (this.isAnyPopupOpen()) {
                popup.close();
            }
        },



        _resetStateRecursive: function (widget) {
            var states = ["hovering", "active"];

            // For each state, set it to false if it is true.
            states.forEach(function (state) {
                if (widget.get(state) === true) {
                    widget.set(state, false);
                }
            });

            if (widget.getChildren && widget.getChildren().length > 0) {
                this._resetStateOfChildren(widget);
            }
        },

        _resetStateOfChildren: function (widget) {
            widget.getChildren().forEach(function (child) {
                this._resetStateRecursive(child);
            }, this);
        },


        /**
         * Refill the contents of the gallery with the updated items in gallery popup.
         * @private
         */
        refreshGallery: function (galleryId) {

            var galleryWidget = registry.byId(galleryId);

            if (!galleryWidget) {
                return;
            }

            // In MATLAB Driven case, it could be that refresh is called but not all of the Gallery Items in the
            // corresponding Gallery Popup have their action peer nodes synced yet. Skip this refresh in that case.
            if (this._modelManager.isMATLABDriven) {
                var galleryPopupId = this._modelManager.get(galleryId, "galleryPopupId");
                if (galleryPopupId) {
                    if (!this._modelManager.manager.hasById(galleryPopupId)) {
                        return;
                    }
                    var galleryPopupNode = this._modelManager.manager.getById(galleryPopupId);
                    if (this._modelManager.manager.getByType(TypeConstants.GALLERY_ITEM, galleryPopupNode)
                        .concat(this._modelManager.manager.getByType(TypeConstants.TOGGLE_GALLERY_ITEM, galleryPopupNode))
                        .some(function (node) {
                            return !this._modelManager.actionService.getAction(
                                node.getProperty("actionId"));
                        }, this)) {
                        return;
                    }
                }
            }

            if (galleryWidget.get("shouldRefresh") === false) {
                return;
            }

            var uniqueItems = [];
            if (galleryWidget.get("hideDisabledItems") === true) {
                uniqueItems = this._getUniqueGalleryItems(galleryId, true);

                if (uniqueItems.length === 0) {
                    uniqueItems = this._getUniqueGalleryItems(galleryId);
                }
            } else {
                uniqueItems = this._getUniqueGalleryItems(galleryId);
            }

            galleryWidget.destroyDescendants();
            uniqueItems.forEach(function (galleryItemPeerNode) {
                var widgetProps = this._getWidgetProperties(galleryItemPeerNode);
                var widget = this._buildWidget(widgetProps, galleryItemPeerNode);
                galleryWidget.addChild(widget);
            }, this);

            if (uniqueItems.length === 0) {
                galleryWidget.closeDropDown();
            }
        },

        /**
         * Fetch the first n (currentColumnCount * rowCount) number of items.
         * First start filling with the favorites, if they are less than n, then add items from
         * other categories in order.
         * @private
         */
        _getUniqueGalleryItems: function (galleryId, onlyEnabledItems) {
            var galleryNode, galleryPopupNode, items = [], n, categories, catItems,
                shouldPush;

            galleryNode = this._modelManager.manager.getById(galleryId);
            galleryPopupNode = this._modelManager.manager.getById(galleryNode.getProperty("galleryPopupId"));
            // Calculate the number of items to be displayed in the gallery:
            n = galleryNode.getProperty("currentColumnCount") * galleryNode.getProperty("galleryItemRowCount");

            var favoriteCategoryId = galleryPopupNode.getProperty("favCategoryId");

            if (this._modelManager.exists(favoriteCategoryId)) {
                // Fetch the favorite items:
                items = this._modelManager.manager.getById(favoriteCategoryId).getChildren();
            }

            if (onlyEnabledItems) {
                items = items.filter(function (item) {
                    return this._modelManager.get(item.getId(), "enabled") === true;
                }, this);
            }

            // We need to check whether the item has already been added by either favorites or some
            // other category.
            function pushItem(catItem) {
                shouldPush = !items.some(function (item) {
                    return item.getProperty("actionId") === catItem.getProperty("actionId");
                });

                if (onlyEnabledItems) {
                    shouldPush = shouldPush && this._modelManager.get(catItem.getId(), "enabled") === true;
                }

                if (shouldPush) {
                    items.push(catItem);
                }
            }

            function fillItemsFromCategories() {
                // Fetch all the categories in descending order of index, since we're popping off
                // categories.
                // We need to start filling in items starting from categories at index 1.
                categories = this._modelManager.getAllCategoriesInDescendingOrder(galleryPopupNode);

                // Until we have the required number of items or we run out of categories, do this:
                while (categories.length > 0) {
                    // Find the items who are children of this category:
                    catItems = categories.pop().getChildren();
                    catItems.forEach(pushItem, this);
                }
            }

            function findFirstSelectedItemInGalleryPopup() {
                var i = 0, length = items.length, result = null;

                while (i < length) {
                    if (this._modelManager.get(items[i].getId(), "selected") === true) {
                        result = {peerNode: items[i], index: i};
                        break;
                    }
                    i += 1;
                }

                return result;
            }

            if (this._modelManager.get(galleryId, "scrollToSelection")) {

                fillItemsFromCategories.call(this);

                var selectedItem = findFirstSelectedItemInGalleryPopup.call(this);

                if (selectedItem) {
                    var selectedItemId = selectedItem.peerNode.getId();
                    var parentCategoryId = this._modelManager.getParentId(selectedItemId);
                    // Make the starting index as the first item in the parent category of the selected item
                    var start = items.indexOf(this._modelManager.manager.getById(this._modelManager.getChildrenIds(parentCategoryId)[0]));

                    // If the selected item doesn't happen to fit in the Gallery view then shift the starting index to
                    // the right to ensure that we show the selected item
                    if (selectedItem.index > (start + n - 1)) {
                        start = selectedItem.index - n + 1;
                    }

                    // If the starting index is such that the Gallery view would overflow then shift the starting index
                    // to the left to ensure that we show enough items in the view
                    if ((start + n) > items.length) {
                        start = items.length - n;
                    }

                    if (start < 0) {
                        start = 0;
                    }

                    return items.splice(start, n);

                } else {
                    // If there are no selected items then show the first n items in the Gallery
                    return items.slice(0, n);
                }
            } else {

                // If the number of favorites is less than the required number of items:
                if (items.length < n) {
                    fillItemsFromCategories.call(this);
                }

                // Return the first n items. They might become more than n if:
                // the favorites are more than n, or
                // since categories are being iterated and their items are being appended.
                return items.slice(0, n);
            }
        },

        /**
         * Cleanup method.
         */
        cleanup: function () {
            Object.keys(this._aspectMap).forEach(function (aspectKey) {
                this._removeAspect(aspectKey);
            }.bind(this));
        }

    });
});

